import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import type {BetterSystemStyleObject} from '@primer/styled-react'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import {Suspense} from 'react'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {TEST_IDS} from '../../constants/test-ids'
import {ContentWrapper} from '../ContentWrapper'
import type {OptionConfig} from '../OptionConfig'
import type {TaskListStatusFragment$key} from '../shared/__generated__/TaskListStatusFragment.graphql'
import type {HeaderBlockedBySummary$key} from './__generated__/HeaderBlockedBySummary.graphql'
import type {HeaderMetadata$key} from './__generated__/HeaderMetadata.graphql'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'
import type {HeaderSubIssueSummary$key} from './__generated__/HeaderSubIssueSummary.graphql'
import type {IssueMetadata$key} from './__generated__/IssueMetadata.graphql'
import type {TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'
import {FIXED_HEADER_HEIGHT} from './constants/sticky-elements'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import {HeaderIssueType, SmallHeaderIssueType} from './HeaderIssueType'
import {type HeaderMenuBaseProps, StickyHeaderMenu} from './HeaderMenu'
import styles from './HeaderMetadata.module.css'
import {HeaderParentTitle} from './HeaderParentTitle'
import {HeaderState} from './HeaderState'
import {HeaderSubIssueSummary} from './HeaderSubIssueSummary'
import {IssueMetadata} from './IssueMetadata'
import {LinkedPullRequestsInternal} from './LinkedPullRequests'
import {RepositoryPill, SmallRepositoryPill} from './RepositoryPill'
import {StickyHeaderTitle} from './StickyHeaderTitle'
import {SmallTrackedBy, TrackedBy} from './TrackedBy'

export type HeaderMetadataProps = {
  headerMetadataKey: HeaderMetadata$key
  optionConfig: OptionConfig
  isSticky: boolean
  stickyStyles: BetterSystemStyleObject
  headerMetadataSecondaryKey?:
    | TaskListStatusFragment$key
    | TrackedByFragment$key
    | HeaderBlockedBySummary$key
    | HeaderParentTitle$key
    | HeaderSubIssueSummary$key
    | IssueMetadata$key
} & HeaderMenuBaseProps

export function HeaderMetadata({
  headerMetadataKey,
  optionConfig,
  isSticky,
  containerRef,
  headerMetadataSecondaryKey,
}: HeaderMetadataProps) {
  const {showRepositoryPill, innerSx} = optionConfig
  const issue_dependencies = useFeatureFlag('issue_dependencies')

  const headerData = useFragment(
    graphql`
      fragment HeaderMetadata on Issue {
        url
        ...HeaderIssueType
        ...HeaderMenu
        ...HeaderState
        ...StickyHeaderTitle
        ...RepositoryPill
        ...LinkedPullRequests
        ...HeaderSubIssueSummaryWithPrimary
        ...IssueMetadata
      }
    `,
    headerMetadataKey,
  )

  const breakpoint = useContainerBreakpoint(containerRef?.current ?? null)

  const classNames = []
  if (optionConfig.insideSidePanel) {
    classNames.push('js-second-sticky-header')
  }
  return (
    <>
      {/* Regular header */}
      <ContentWrapper sx={innerSx}>
        <Box
          data-testid={TEST_IDS.issueMetadataFixed}
          sx={{
            height: '100%',
            mb: isSticky ? `-${FIXED_HEADER_HEIGHT}px` : undefined,
            minHeight: `${FIXED_HEADER_HEIGHT}px`,
            width: '100%',
          }}
          className={styles.metadataContainer}
        >
          <div className={styles.metadataBorder}>
            <div className={styles.metadataContent}>
              <HeaderState isSticky={isSticky} stateData={headerData} />
              <HeaderIssueType data={headerData} />

              <HeaderSubIssueSummary
                subIssuePrimaryKey={headerData}
                subIssueSecondaryKey={headerMetadataSecondaryKey as HeaderSubIssueSummary$key}
              />

              {issue_dependencies && (
                <HeaderBlockedBySummary
                  blockedBySecondaryKey={headerMetadataSecondaryKey as HeaderBlockedBySummary$key}
                />
              )}

              <HeaderParentTitle
                parentKey={headerMetadataSecondaryKey as HeaderParentTitle$key}
                optionConfig={optionConfig}
              />

              <Suspense>
                <TrackedBy url={headerData.url} trackedByKey={headerMetadataSecondaryKey as TrackedByFragment$key} />
              </Suspense>

              <LinkedPullRequestsInternal issueData={headerData} />
              {showRepositoryPill && <RepositoryPill repositoryPillData={headerData} />}
            </div>
          </div>
        </Box>
      </ContentWrapper>
      {/* "Sticky" header */}
      <Box
        data-testid={TEST_IDS.issueMetadataSticky}
        sx={{
          display: isSticky ? 'flex' : 'none',
          height: `${FIXED_HEADER_HEIGHT}px`,
          justifyContent: 'center',
          position: 'sticky',
          width: '100%',
          zIndex: 14,
          top: 'var(--base-sticky-header-height, 0)',
        }}
        id="issue-viewer-sticky-header"
        className={clsx(classNames, styles.stickyContainer)}
      >
        <ContentWrapper sx={innerSx}>
          <div className={styles.stickyContent}>
            <HeaderState isSticky={isSticky} stateData={headerData} />
            <div className={styles.titleAndMetadata}>
              <StickyHeaderTitle headerTitleData={headerData} scrollToTopOnClick={optionConfig.scrollToTopOnClick} />
              <Box
                sx={{
                  display: optionConfig.useViewportQueries
                    ? ['none', 'none', 'flex', 'flex']
                    : breakpoint(['none', 'none', 'flex', 'flex']),

                  '& > *:not(:last-child)::after': {},
                }}
                className={styles.smallMetadataRow}
              >
                <SmallHeaderIssueType data={headerData} />
                <HeaderSubIssueSummary
                  subIssuePrimaryKey={headerData}
                  subIssueSecondaryKey={headerMetadataSecondaryKey as HeaderSubIssueSummary$key}
                  size="small"
                />
                {issue_dependencies && (
                  <HeaderBlockedBySummary
                    blockedBySecondaryKey={headerMetadataSecondaryKey as HeaderBlockedBySummary$key}
                    size="small"
                  />
                )}
                <HeaderParentTitle
                  parentKey={headerMetadataSecondaryKey as HeaderParentTitle$key}
                  optionConfig={optionConfig}
                  small
                />
                <Suspense>
                  <SmallTrackedBy
                    url={headerData.url}
                    trackedByKey={headerMetadataSecondaryKey as TrackedByFragment$key}
                  />
                </Suspense>
                <LinkedPullRequestsInternal issueData={headerData} isSmall />
                {showRepositoryPill && <SmallRepositoryPill repositoryPillData={headerData} />}
              </Box>
            </div>
            <div>
              <StickyHeaderMenu optionConfig={optionConfig} headerMenuData={headerData} containerRef={containerRef} />
            </div>
          </div>
        </ContentWrapper>
      </Box>
      <IssueMetadata metadataKey={headerData} />
    </>
  )
}

try{ HeaderMetadata.displayName ||= 'HeaderMetadata' } catch {}